/**
 * custom-infinite-scroll.js
 * -----------------------------------------------------------------------------
 * Load-More-on-Scroll hybrid for Prestige collection pages.
 *
 * Reuses Prestige's own section-rendering pattern (?section_id=...) to fetch the
 * next page, then APPENDS the new product cards to the existing <product-list>
 * instead of replacing them (which is what the native facet:update handler does).
 *
 * Modes (data-mode):
 *   - "load_more": manual button only. User clicks "Load More" each time.
 *   - "infinite":  auto-loads the next page on scroll up to data-auto-load-limit
 *                  pages, then falls back to the manual "Load More" button. This
 *                  keeps the footer reachable and is gentler on SEO/UX.
 *
 * The custom element re-initializes automatically when filters/sort re-render the
 * section (Prestige replaces the whole .shopify-section on facet:update).
 */
if (!window.customElements.get('infinite-scroll')) {
  class InfiniteScroll extends HTMLElement {
    connectedCallback() {
      this.productList = document.getElementById(this.dataset.productList);
      this.button = this.querySelector('[data-load-more]');
      this.sentinel = this.querySelector('[data-sentinel]');
      this.statusEl = this.querySelector('[data-status]');

      this.mode = this.dataset.mode || 'infinite';
      this.autoLoadLimit = parseInt(this.dataset.autoLoadLimit, 10) || 0;
      this.nextUrl = this.dataset.nextUrl || null;

      this.autoLoaded = 0;
      this.loading = false;

      if (!this.productList || !this.nextUrl) {
        this.hidden = true;
        return;
      }

      if (this.button) {
        this.button.addEventListener('click', () => this.loadNext());
      }

      if (this.mode === 'infinite' && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.maybeAutoLoad();
          },
          { rootMargin: '800px 0px' }
        );
        this.observer.observe(this.sentinel);
      }

      this.updateButton();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    get canAutoLoad() {
      return this.mode === 'infinite' && this.autoLoaded < this.autoLoadLimit;
    }

    maybeAutoLoad() {
      if (this.canAutoLoad && !this.loading) {
        this.autoLoaded += 1;
        this.loadNext();
      }
    }

    updateButton() {
      if (!this.button) return;
      // Show the manual button whenever there is a next page and we are NOT going
      // to auto-load it (manual mode, or the auto-load budget is spent).
      const showButton = Boolean(this.nextUrl) && (this.mode === 'load_more' || !this.canAutoLoad);
      this.button.hidden = !showButton;
    }

    setBusy(state) {
      this.loading = state;
      document.documentElement.dispatchEvent(
        new CustomEvent(state ? 'theme:loading:start' : 'theme:loading:end', { bubbles: true })
      );
      if (this.button) {
        this.button.setAttribute('aria-busy', String(state));
        this.button.disabled = state;
      }
      if (this.statusEl) this.statusEl.hidden = !state;
    }

    async loadNext() {
      if (this.loading || !this.nextUrl) return;
      this.setBusy(true);

      try {
        const url = new URL(this.nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);

        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fetchedList =
          doc.getElementById(this.dataset.productList) || doc.querySelector('product-list');

        if (fetchedList) {
          const fragment = document.createDocumentFragment();
          Array.from(fetchedList.children).forEach((node) => {
            // Cards carry reveal-on-scroll="true" (opacity:0 until the list's
            // inView fires). The list is already in view, so strip it to ensure
            // appended cards are visible immediately.
            if (node.matches && node.matches('[reveal-on-scroll="true"]')) {
              node.removeAttribute('reveal-on-scroll');
            }
            node.querySelectorAll &&
              node
                .querySelectorAll('[reveal-on-scroll="true"]')
                .forEach((el) => el.removeAttribute('reveal-on-scroll'));
            fragment.appendChild(node);
          });
          this.productList.appendChild(fragment);
        }

        // Most reliable source for the following page: the re-rendered
        // <infinite-scroll> element in the fetched markup carries the exact
        // paginate.next.url. Fall back to the noscript numbered pagination.
        const fetchedScroller = doc.querySelector('infinite-scroll');
        const fallbackNext = doc.querySelector('.pagination a[rel="next"]');
        this.nextUrl =
          (fetchedScroller && fetchedScroller.dataset.nextUrl) ||
          (fallbackNext && fallbackNext.getAttribute('href')) ||
          null;
      } catch (error) {
        // Leave nextUrl intact so the user can retry via the button.
        console.warn('[infinite-scroll] failed to load next page:', error);
      } finally {
        this.setBusy(false);

        if (!this.nextUrl) {
          // No more pages — tear everything down.
          this.observer?.disconnect();
          if (this.button) this.button.hidden = true;
        } else {
          this.updateButton();

          if (this.observer) {
            if (this.canAutoLoad) {
              // The sentinel may still be in view after appending (it only moved
              // down). IntersectionObserver fires on CHANGES, so re-observe to get
              // a fresh callback and keep auto-loading consecutive in-view pages.
              this.observer.unobserve(this.sentinel);
              this.observer.observe(this.sentinel);
            } else {
              // Auto-load budget spent — hand off to the manual Load More button.
              this.observer.disconnect();
            }
          }
        }
      }
    }
  }

  window.customElements.define('infinite-scroll', InfiniteScroll);
}
